import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations } from '../data/translations';
import { INVESTMENT_TYPES, getTaxConfig } from '../utils/taxEngine';

const AppContext = createContext();

const EXCHANGE_RATE = 5.0;

// ─── Default settings per instrument type ────────────────────────────────────
const DEFAULT_PER_TYPE = {
  [INVESTMENT_TYPES.SAVINGS_ACCOUNT]: {
    initialDeposit: 10000,
    monthlyContribution: 500,
    annualInterestRate: 2.5,
    years: 20,
    compoundingFrequency: 12,
    contributionFrequency: 12,
    includeTaxes: true,
    adminFeeRate: 0,
    pauseEnabled: false,
    pauseStartYear: 5,
    pauseEndYear: 7,
    topUps: [],
    withdrawals: [],
  },
  [INVESTMENT_TYPES.ACCUMULATION_PLAN]: {
    initialDeposit: 10000,
    monthlyContribution: 500,
    annualInterestRate: 7,
    years: 20,
    compoundingFrequency: 12,
    contributionFrequency: 12,
    includeTaxes: false,
    adminFeeRate: 1.5,
    pauseEnabled: false,
    pauseStartYear: 5,
    pauseEndYear: 7,
    topUps: [],
    withdrawals: [],
  },
  [INVESTMENT_TYPES.CHILD_FUTURE]: {
    initialDeposit: 5000,
    monthlyContribution: 300,
    annualInterestRate: 7,
    years: 18,
    compoundingFrequency: 12,
    contributionFrequency: 12,
    includeTaxes: false,
    adminFeeRate: 1.5,
    pauseEnabled: false,
    pauseStartYear: 5,
    pauseEndYear: 7,
    topUps: [],
    withdrawals: [],
    childAge: 3,
    parentAge: 35,
  },
  [INVESTMENT_TYPES.CREDIT]: {
    loanAmount: 200000,
    creditTermMonths: 300,
    creditRate: 5,
    monthlyFeeRate: 0.03,
    gracePeriodMonths: 0,
    repaymentType: 'annuity',
    extraMonthlyPayment: 0,
  },
};

export const AppProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('theme') || 'light';
    return 'light';
  });
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('language') || 'ro';
    return 'ro';
  });
  const [currency, setCurrency] = useState('RON');

  // Active instrument type
  const [investmentType, setInvestmentType] = useState(INVESTMENT_TYPES.ACCUMULATION_PLAN);

  // Per-type settings store — each type remembers its own values
  const [perTypeSettings, setPerTypeSettings] = useState(() => {
    // Deep clone defaults
    return JSON.parse(JSON.stringify(DEFAULT_PER_TYPE));
  });

  // Convenience: current inputs = settings for active type
  const inputs = {
    ...perTypeSettings[investmentType],
    investmentType,
  };

  useEffect(() => {
    const root = document.documentElement;
    theme === 'dark' ? root.classList.add('dark') : root.classList.remove('dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const toggleTheme = useCallback(() => setTheme(p => p === 'light' ? 'dark' : 'light'), []);
  const toggleLanguage = useCallback(() => setLanguage(p => p === 'ro' ? 'en' : 'ro'), []);
  const toggleCurrency = useCallback(() => setCurrency(p => p === 'RON' ? 'EUR' : 'RON'), []);

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations.ro[key] || key;
  }, [language]);

  const formatMoney = useCallback((value) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    const displayValue = currency === 'EUR' ? value / EXCHANGE_RATE : value;
    return new Intl.NumberFormat(language === 'ro' ? 'ro-RO' : 'en-US', {
      style: 'currency',
      currency: currency === 'EUR' ? 'EUR' : 'RON',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(displayValue);
  }, [currency, language]);

  // updateInput — updates ONLY the active type's settings
  const updateInput = useCallback((key, value) => {
    if (key === 'investmentType') {
      setInvestmentType(value);
      return;
    }
    setPerTypeSettings(prev => ({
      ...prev,
      [investmentType]: {
        ...prev[investmentType],
        [key]: value,
      },
    }));
  }, [investmentType]);

  // setInputs — replace entire current type settings (used for bulk reset)
  const setInputs = useCallback((newInputs) => {
    const { investmentType: newType, ...rest } = newInputs;
    if (newType) setInvestmentType(newType);
    setPerTypeSettings(prev => ({
      ...prev,
      [newType || investmentType]: rest,
    }));
  }, [investmentType]);

  // resetInputs — reset current type to defaults
  const resetInputs = useCallback(() => {
    setPerTypeSettings(prev => ({
      ...prev,
      [investmentType]: JSON.parse(JSON.stringify(DEFAULT_PER_TYPE[investmentType])),
    }));
  }, [investmentType]);

  // getTypeSettings — get settings for any type (used by StrategyComparison)
  const getTypeSettings = useCallback((type) => {
    return {
      ...perTypeSettings[type],
      investmentType: type,
    };
  }, [perTypeSettings]);

  const taxConfig = getTaxConfig(investmentType);

  const value = {
    theme, setTheme, toggleTheme,
    language, setLanguage, toggleLanguage, t,
    currency, setCurrency, toggleCurrency, formatMoney, EXCHANGE_RATE,
    inputs,
    setInputs,
    updateInput,
    resetInputs,
    getTypeSettings,
    perTypeSettings,
    taxConfig,
    investmentType,
    setInvestmentType,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};

export default AppContext;
